/* eslint-disable no-underscore-dangle */
import type { VoidFunctionComponent } from 'react';
import shortid from 'shortid';
import AccessGate from '../../../generic/Gates/AccessGate';
import Button from '../../../generic/Button';
import { IconText, icons } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import Repository from './Repository';
import Server from './Server';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import { dropAt } from '../../../../utils';
import { window } from '../../../../globals';
import type { MavenFetcherConfig, MavenRepository, MavenServer } from '../../../../../typings/common';

/**
 * Maven settings page.
 */
const JavaSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'mvn',
  );

  const repositories = settings?.repositories || [];
  const servers = settings?.servers || [];

  const onAdd = (configKey: keyof MavenFetcherConfig) => {
    onUpdateSettings({
      [configKey]: [...(settings?.[configKey] || []), { _id: shortid.generate() }],
    });
  };

  const onRemove = (configKey: keyof MavenFetcherConfig, index: number) => {
    const configSourceValues: MavenFetcherConfig[typeof configKey] = settings?.[configKey] || [];
    onUpdateSettings({
      [configKey]: dropAt(configSourceValues, index),
    });
  };

  const onUpdateServer = <K extends keyof MavenServer>(
    index: number,
    member: K,
    value: MavenServer[K],
  ) => {
    onUpdateSettings({
      servers: servers.map((server, i) => {
        if (i === index) {
          return {
            ...server,
            [member]: value,
            hasPassword: member === 'password' ? false : server.hasPassword,
          };
        }
        return server;
      }),
    });
  };

  const onUpdateRepository = <K extends keyof MavenRepository>(
    index: number,
    member: K,
    value: MavenRepository[K],
  ) => {
    onUpdateSettings({
      repositories: repositories.map((repo, i) => {
        if (i === index) {
          return {
            ...repo,
            [member]: value,
          };
        }
        return repo;
      }),
    });
  };

  return (
    <AccessGate level="premium">
      <Header heading="Maven Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <>
            <Section>
              <Section.Left heading="Repositories">
                <Section.Paragraph>
                  Configure Maven repository settings for your organization.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right>
                {repositories.map((repository, index) =>
                  <Repository
                    key={repository._id}
                    id={repository.id}
                    url={repository.url}
                    onRemove={() => onRemove('repositories', index)}
                    onUpdateRepository={
                      <K extends keyof MavenRepository>(
                        member: K,
                        value: MavenRepository[K],
                      ) => onUpdateRepository(index, member, value)
                    }
                  />,
                )}
                <Button
                  type="secondary"
                  size="medium"
                  onClick={() => onAdd('repositories')}
                >
                  <IconText icon={icons.plus} text="Add" />
                </Button>
              </Section.Right>
            </Section>
            <Section>
              <Section.Left heading="Servers">
                <Section.Paragraph>
                  Configure Maven server settings for your organization.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right>
                {servers.map((server, index) =>
                  <Server
                    key={server._id}
                    id={server.id}
                    username={server.username}
                    password={server.password}
                    hasPassword={server.hasPassword}
                    onRemove={() => onRemove('servers', index)}
                    onUpdateServer={
                      <K extends keyof MavenServer>(
                        member: K,
                        value: MavenServer[K],
                      ) => onUpdateServer(index, member, value)
                    }
                  />,
                )}
                <Button
                  type="secondary"
                  size="medium"
                  onClick={() => onAdd('servers')}
                >
                  <IconText icon={icons.plus} text="Add" />
                </Button>
              </Section.Right>
            </Section>
          </>
          )}
    </AccessGate>
  );
};

export default JavaSettings;
